import type { IncomingMessage, ServerResponse } from 'http';
import { Result } from '@commercetools/frontend-domain-types/product/Result';
import { ServerOptions } from '@commercetools/frontend-sdk';
import { AcceptedQueryTypes } from '@commercetools/frontend-sdk/lib/types/Query';
import { sdk } from 'sdk';
import { PageDataResponse, PagePreviewDataResponse, RedirectResponse, PageFolderStructureResponse } from '../types';

export const getRouteData =
  () =>
  async (
    slug: string[],
    query?: AcceptedQueryTypes,
    req?: IncomingMessage,
    res?: ServerResponse,
  ): Promise<RedirectResponse | PageDataResponse> => {
    const pageSlug = (slug as string[])?.join('/') || '';
    const path = `/${pageSlug !== 'index' ? encodeURI(pageSlug) : ''}`;
    
    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await sdk.page.getPage({
      path,
      query,
      serverOptions: req && res ? serverOptions : undefined,
    });

    if (response.isError) {
      const errorMessage = response.error?.message;
      // Check if the error is specifically about not being able to resolve page from path
      if (errorMessage?.includes('Error: Could not resolve page from path')) {
        // Return empty response for legitimate 404s instead of throwing error
        return {} as PageDataResponse;
      } else {
        // For other errors, throw to show 500 error page
        throw new Error(`Frontastic API error: ${errorMessage || 'Unknown error'}`);
      }
    }

    const redirectTo = (response.data?.data?.dataSources?.__master as any)?.redirectTo as string;
    if (redirectTo) return { target: redirectTo, statusCode: 301 } as RedirectResponse;

    return response.data as unknown as RedirectResponse | PageDataResponse;
  };

export const getPreview =
  () =>
  async (previewId: string): Promise<PagePreviewDataResponse> => {
    const res = await sdk.page.getPreview({ previewId });

    return (res.isError ? {} : res.data) as PagePreviewDataResponse;
  };

export const getCategories =
  () =>
  async (req?: IncomingMessage, res?: ServerResponse): Promise<Result> => {
    const extensions = sdk.composableCommerce;

    const serverOptions: ServerOptions = {
      req: req,
      res: res,
    };

    const response = await extensions.product.queryCategories({ limit: 99 }, { serverOptions: serverOptions });

    if (response.isError) {
      throw new Error(`Error from getCategories: ${JSON.stringify(response)}`);
    }

    return response.data as Result;
  };

export const getStructure =
  () =>
  async (path: string, depth: number): Promise<PageFolderStructureResponse> => {
    const res = await sdk.page.getPages({ path, depth });

    return (res.isError ? {} : res.data) as PageFolderStructureResponse;
  };
